import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import EmployeePageShell from "./EmployeePageShell";
import useFetch from "../../useFetch";
import { getStoredUser } from "../../auth";
import { apiRequest } from "../../utils/api";
import FormFeedback, { FieldError, inputErrorClass } from "../Common/FormFeedback";
import AccountReferralCard from "../Common/AccountReferralCard";

const EmployeeCompanyProfile = () => {
  const formTopRef = useRef(null);
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const storedUser = getStoredUser();

  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [industry, setIndustry] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [foundedYear, setFoundedYear] = useState("");
  const [overview, setOverview] = useState("");
  const [culture, setCulture] = useState("");
  const [logo, setLogo] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  const { response, isLoading: isLoadingFetch, error: fetchError } = useFetch(process.env.REACT_APP_BASE_API_URL + "/company-profile");
  const account = response?.account ?? storedUser;

  const normalizeWebsite = (value) => {
    const trimmed = value.trim();

    if (!trimmed || ["none", "n/a", "na", "no"].includes(trimmed.toLowerCase())) {
      return "";
    }

    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  useEffect(() => {
    if (!response?.company_profile) return;

    const profile = response.company_profile;

    setCompanyName(profile.company_name ?? "");
    setWebsite(profile.website ?? "");
    setEmail(profile.email ?? "");
    setPhone(profile.phone ?? "");
    setLocation(profile.location ?? "");
    setIndustry(profile.industry ?? "");
    setCompanySize(profile.company_size ?? "");
    setFoundedYear(profile.founded_year ?? "");
    setOverview(profile.overview ?? "");
    setCulture(profile.culture ?? "");
    setLogoPreview(profile.logo_url ?? null);
    setCoverPreview(profile.cover_image_url ?? null);
  }, [response]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0] ?? null;

    setLogo(file);
    setLogoPreview(file ? URL.createObjectURL(file) : response?.company_profile?.logo_url ?? null);
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0] ?? null;

    setCoverImage(file);
    setCoverPreview(file ? URL.createObjectURL(file) : response?.company_profile?.cover_image_url ?? null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setIsLoading(true);
    setSuccess(false);
    setError(false);
    setValidationErrors([]);
    setFieldErrors({});

    let formData = new FormData();
    formData.append("company_name", companyName);
    formData.append("website", normalizeWebsite(website));
    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("location", location);
    formData.append("industry", industry);
    formData.append("company_size", companySize);
    formData.append("founded_year", foundedYear);
    formData.append("overview", overview);
    formData.append("culture", culture);
    if (logo) formData.append("logo", logo);
    if (coverImage) formData.append("cover_image", coverImage);

    apiRequest("/company-profile", {
      method: "POST",
      body: formData,
    })
      .then(data => {
        formTopRef.current?.scrollIntoView();
        setIsLoading(false);

        if (data?.status === "success") {
          setSuccess(data.message);
          setLogo(null);
          setCoverImage(null);
          setLogoPreview(data.company_profile?.logo_url ?? null);
          setCoverPreview(data.company_profile?.cover_image_url ?? null);
        }
        else if (data?.status === "validation_error") {
          setValidationErrors(data.messages);
          setFieldErrors(data.errors ?? {});
        }
        else if (data?.status === "error" && data?.message) {
          setError(data.message);
        }
        else {
          setError("There was some error while saving company profile, please try again.");
        }
      })
      .catch(error => {
        formTopRef.current?.scrollIntoView();
        setIsLoading(false);
        if (error.data?.status === "validation_error") {
          setValidationErrors(error.messages);
          setFieldErrors(error.errors);
        }
        else {
          setError(error.message);
        }
      });
  };

  return (
    <EmployeePageShell
      active="profile"
      title="Company Profile"
      text="Keep your employer profile clear for candidates."
    >
      <div className="ls-widget employee-form-widget">
        <div ref={formTopRef}></div>
        <div className="widget-title">
          <h4>Basic Company Details</h4>
          <Link to="/employee-view-company-profile" className="theme-btn btn-style-three">View Profile</Link>
        </div>
        <div className="widget-content">
          <FormFeedback success={success} error={error} validationErrors={validationErrors} />
          {fetchError && <div className="alert alert-danger"><strong>{fetchError}</strong></div>}
          {isLoadingFetch && <h5 className="text-center my-3">Loading Data</h5>}
          <AccountReferralCard account={account} className="profile-account-referral" />

          <form className="default-form" onSubmit={handleSubmit}>
            <div className="row">
              <div className="form-group col-lg-6 col-md-12">
                <label>Company Logo</label>
                <div className="employee-upload-card employee-upload-card-logo">
                  <div className="employee-upload-preview">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Company logo preview" />
                    ) : (
                      <span className="la la-building"></span>
                    )}
                  </div>
                  <div className="employee-upload-content">
                    <strong>{logo ? logo.name : logoPreview ? "Logo uploaded" : "Upload company logo"}</strong>
                    <span>Square image works best. JPG or PNG, max 2MB.</span>
                    <button type="button" className="theme-btn btn-style-three" onClick={() => logoInputRef.current?.click()}>
                      Choose Logo
                    </button>
                  </div>
                  <input ref={logoInputRef} className="employee-upload-input" type="file" accept="image/*" onChange={handleLogoChange} />
                </div>
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Cover Image</label>
                <div className="employee-upload-card employee-upload-card-cover">
                  <div className="employee-upload-preview">
                    {coverPreview ? (
                      <img src={coverPreview} alt="Company cover preview" />
                    ) : (
                      <span className="la la-image"></span>
                    )}
                  </div>
                  <div className="employee-upload-content">
                    <strong>{coverImage ? coverImage.name : coverPreview ? "Cover image uploaded" : "Upload cover image"}</strong>
                    <span>Wide banner image. JPG or PNG, max 4MB.</span>
                    <button type="button" className="theme-btn btn-style-three" onClick={() => coverInputRef.current?.click()}>
                      Choose Cover
                    </button>
                  </div>
                  <input ref={coverInputRef} className="employee-upload-input" type="file" accept="image/*" onChange={handleCoverChange} />
                </div>
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Company Name</label>
                <input type="text" className={inputErrorClass(fieldErrors, "company_name")} placeholder="BrightTech" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
                <FieldError errors={fieldErrors} name="company_name" />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Website</label>
                <input type="text" className={inputErrorClass(fieldErrors, "website")} placeholder="brighttech.com or none" value={website} onChange={(e) => setWebsite(e.target.value)} />
                <FieldError errors={fieldErrors} name="website" />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Email</label>
                <input type="email" className={inputErrorClass(fieldErrors, "email")} placeholder="hiring@brighttech.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                <FieldError errors={fieldErrors} name="email" />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Phone</label>
                <input type="text" placeholder="+1 234 567 890" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Location</label>
                <input type="text" placeholder="London, UK" value={location} onChange={(e) => setLocation(e.target.value)} />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Industry</label>
                <input type="text" placeholder="Software" value={industry} onChange={(e) => setIndustry(e.target.value)} />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Company Size</label>
                <input type="text" placeholder="51-200 employees" value={companySize} onChange={(e) => setCompanySize(e.target.value)} />
              </div>
              <div className="form-group col-lg-6 col-md-12">
                <label>Founded Year</label>
                <input type="text" placeholder="2018" value={foundedYear} onChange={(e) => setFoundedYear(e.target.value)} />
              </div>
              <div className="form-group col-lg-12">
                <label>Company Overview</label>
                <textarea placeholder="Write a short overview about your company, hiring focus, and work culture." value={overview} onChange={(e) => setOverview(e.target.value)}></textarea>
              </div>
              <div className="form-group col-lg-12">
                <label>Culture & Benefits</label>
                <textarea placeholder="Tell candidates about your team culture, values, and benefits." value={culture} onChange={(e) => setCulture(e.target.value)}></textarea>
              </div>
              <div className="form-group col-lg-12">
                <button type="submit" className="theme-btn btn-style-one" disabled={isLoading}>
                  {isLoading ? "Loading..." : "Save Company Profile"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </EmployeePageShell>
  );
};

export default EmployeeCompanyProfile;
